import React, { forwardRef, useEffect, useRef, useImperativeHandle } from "react";
import { net, Player, polyfill, util } from "shaka-player";
import { stepFrame } from "../../util/video";

const CustomPlayer = forwardRef(
  (
    {
      url,
      controls = true,
      playing = true,
      muted = false,
      volume = 1,
      loop = false,
      drmConfig,
      time = null,
      onProgress = null,
      style = {
        maxHeight: "480px",
      },
      fixedFrame = false,
    },
    ref,
  ) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);

    const authToken = drmConfig && drmConfig.authToken ? drmConfig.authToken : null;

    useEffect(() => {
      const videoElement = videoRef.current;

      const handleError = (error) => {
        if (error instanceof Error) {
          console.error("Error code", error.code, "object", error);
        }

        if (error.severity === util.Error.Severity.CRITICAL) {
          console.error("Error code", error.code, "object", error);
          if (playerRef.current) playerRef.current.destroy();
        } else {
          console.error("Error code", error.code, "object", error);
        }
      };

      const handleTimeUpdate = () => {
        if (videoElement.readyState > 0 && onProgress) {
          onProgress(videoElement.currentTime);
        }
      };

      if (!videoElement) {
        console.error("Video element is unavailable.");
        return;
      }

      async function initPlayer() {
        const player = new Player();
        await player.attach(videoElement);
        playerRef.current = player;

        player.addEventListener("error", handleError);

        if (drmConfig) {
          player.configure({
            drm: {
              servers: drmConfig.servers,
              advanced: drmConfig.advanced,
            },
          });

          player
            .getNetworkingEngine()
            .registerRequestFilter(function (type, request, context) {
              if (type === net.NetworkingEngine.RequestType.LICENSE) {
                request.headers["nv-authorizations"] = drmConfig.authToken;
              }
            });
        }

        try {
          await player.load(url);
          playing ? videoElement.play() : videoElement.pause();
        } catch (e) {
          handleError(e);
        }

        videoElement.addEventListener('timeupdate', handleTimeUpdate);
        videoElement.addEventListener('seeked', handleTimeUpdate);
      }

      polyfill.installAll();

      if (Player.isBrowserSupported()) {
        initPlayer();
      } else {
        console.error("Browser not supported!");
      }

      return () => {
        videoElement.removeEventListener('timeupdate', handleTimeUpdate);
        videoElement.removeEventListener('seeked', handleTimeUpdate);
        if (playerRef.current) {
          playerRef.current.removeEventListener('error', handleError);
          playerRef.current.destroy().then(() => {
            playerRef.current = null;
          });
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, authToken]);

    useEffect(() => {
      if (!videoRef.current || videoRef.current.readyState < 1) return;
      videoRef.current.currentTime = time;
      fixedFrame && videoRef.current.pause();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [time]);

    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.volume = volume;
        videoRef.current.muted = muted;
      }
    }, [volume, muted]);

    useImperativeHandle(ref, () => {
      const step = (dir) => {
        if (videoRef.current && playerRef.current) {
          stepFrame(videoRef.current, playerRef.current, dir);
        }
      };

      return {
        play: () => videoRef.current?.play()?.catch(() => { }),
        pause: () => videoRef.current?.pause(),
        seekTo: (t) => {
          if (videoRef.current) videoRef.current.currentTime = t;
        },
        getCurrentTime: () => videoRef.current?.currentTime ?? 0,
        setVolume: (v) => {
          if (videoRef.current) videoRef.current.volume = v;
        },
        setMuted: (m) => {
          if (videoRef.current) videoRef.current.muted = m;
        },
        stepForward: () => step(1),
        stepBack: () => step(-1),
        // escape hatches
        getVideoElement: () => videoRef.current,
        getShakaPlayer: () => playerRef.current,
      };
    }, []);

    return (
      <video
        ref={videoRef}
        autoPlay={playing}
        controls={controls}
        muted={muted}
        loop={loop}
        style={{ 'maxHeight': '480px', ...style }}
        onClick={() => {
          if (controls || !videoRef.current) return;
          if (videoRef.current.paused) {
            videoRef.current.play();
          } else {
            videoRef.current.pause();
          }
        }}
      >
        <track kind="captions" srcLang="es" label="Spanish captions" />
      </video>
    );
  },
);

export default CustomPlayer;
